<script lang="ts" setup>
import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
defineEmits(["update:value", "update:idsRows"]);
import { computed } from 'vue';
import { TableType } from '.';
import { FormBuilderContract } from '@/components/builder/form';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { NumericContract, Definition as NumericDefinition } from '../numeric';
import { evaluate } from 'mathjs';

const COLUMN_WIDTH = 172;

const { value, idsRows } = __MACROS_useVModel("value", "idsRows");

const props = defineProps({
  "value": null,
  "idsRows": null,
  "blueprint": null,
  "entry": null,
  "form": null,
  "design": { type: Boolean,  }
});

const blueprint = computed(() => props.blueprint);
const columns = computed(() => blueprint.value.columns);
const minWidth = computed(() => (blueprint.value.columns.length ?? 0) * COLUMN_WIDTH + 'px');
const footer = computed(() => blueprint.value.columns.some((item) => item.summary));

const formulas = computed(() =>
{
    return blueprint.value.columns
        .map((item, index) => ({ formula: item.formula, field: blueprint.value.components[index].name }))
        .filter((item) => item.formula && item.field && item.field.toLowerCase().includes(NumericDefinition.type));
});

const getSumOfColumn = (colName: string, precision: number): number =>
{
    if (!props.design)
    {
        const sum = value.value.reduce((acc: number, item: any) =>
        {
            const value = item[colName]?.data;
            const type = item[colName]?.type;

            if (type === NumericDefinition.type) return acc + (value || 0);

            return acc;
        }, 0);

        return +sum.toFixed(precision || 0);
    }

    return 0;
};

const getScope = (index: number, field: string) =>
{
    const mappedScope: Record<string, number> = {};

    for (const key in value.value[index])
    {
        if (key.toLowerCase().includes(NumericDefinition.type) && key != field)
        {
            mappedScope[key] = value.value[index][key].data ?? 0;
        }
    }

    return mappedScope;
};

const removeRow = (index: number) =>
{
    value.value.splice(index, 1);
    idsRows.value.splice(index, 1);
};

const resultFormula = (index: number) =>
{
    const formulasLength = formulas.value.length;

    for (let i = 0; i < formulasLength; i++)
    {
        const formula = formulas.value[i];
        const scope = getScope(index, formula.field);

        if (Object.values(scope)?.length > 0)
        {
            const result = evaluate(formula.formula, scope);

            value.value[index][formula.field].data = result;
        }
    }
};
</script>

<template>
    <div class="scroll scroll-x pb-1">
        <table v-if="columns.length" class="table table-bordered mb-0" :style="{ 'min-width': minWidth }">
            <thead>
                <tr>
                    <th v-for="(col, index) in blueprint.components" :key="col.id">
                        {{ blueprint.columns[index].columnHeader }}
                    </th>
                    <th v-if="idsRows.length && blueprint.canAddRow" width="54"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, index) in idsRows" :key="row">
                    <td
                        v-for="col in blueprint.components"
                        :key="col.id"
                        class="position-relative"
                        :class="!design ? 'p-2 pb-0' : 'p-0'"
                    >
                        <component
                            :is="form.schema.designer(col.type)"
                            :form="form"
                            :parent="blueprint"
                            :blueprint="col"
                            :entry="entry"
                            :index="index"
                            disabled-drag
                            disabled-toolbar
                            @update="resultFormula(index)"
                        ></component>
                    </td>
                    <td v-if="blueprint.canAddRow" class="align-middle">
                        <ideo-button variant="danger" icon="fas fa-trash" :disabled="design" @click="removeRow(index)" />
                    </td>
                </tr>
                <tr v-if="idsRows.length === 0">
                    <td :colspan="blueprint.components.length" class="text-center">
                        <b>{{ $t('[[[Brak wyników]]]') }}</b>
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="footer">
                <tr class="border-0">
                    <td
                        v-for="(col, index) in blueprint.components"
                        :key="col.id"
                        :class="!blueprint.columns[index].summary ? 'border-0' : 'table-border'"
                    >
                        <ideo-input-group
                            v-if="blueprint.columns[index].summary"
                            :prepend="(col as NumericContract).affix.prepend"
                            :append="(col as NumericContract).affix.append"
                        >
                            <input
                                type="number"
                                class="form-control"
                                :value="getSumOfColumn(col.name, (col as NumericContract).precision)"
                                disabled
                            />
                        </ideo-input-group>
                    </td>
                </tr>
            </tfoot>
        </table>
        <p v-else>{{ $t('[[[Brak dodanych kolumn]]]') }}</p>
    </div>
</template>

<style lang="scss" scoped>
.table {
    table-layout: fixed;
    overflow: scroll;

    thead {
        tr {
            height: 36px;

            th {
                background: var(--bs-tertiary-bg);
            }
        }
    }

    &-border {
        background: var(--bs-tertiary-bg);
        border: 1px solid var(--bs-table-border-color);
    }
}

.table-error {
    margin-top: -12px;
}
</style>
